import getPublishingPermissionsService from '../services/getPublishingPermissionsService';
import '../store/publishingStore';
import CalendarPublishingError from '../store/schema/CalendarPublishingError';
import PublishingPermissionItemState from '../store/schema/PublishingPermissionItemState';
import CalendarSharingPermissionInfo from 'owa-service/lib/contract/CalendarSharingPermissionInfo';
import GetCalendarSharingPermissionsResponse from 'owa-service/lib/contract/GetCalendarSharingPermissionsResponse';
import { isConsumer } from 'owa-session-store';
import { orchestrator } from 'satcheljs';
import {
    getAvailablePublishingDetailLevelsFromMaxLevel,
    getConsumerAvailablePublishingDetailLevels,
    getConsumerAvailablePublishingDetailLevelsFromMaxLevel,
} from '../utils/CalendarPublishingUtils';
import {
    saveCalendarPublishingPermissions,
    loadCalendarPublishingPermissions,
    setInitialPublishPermissionsPending,
    setCalendarPublishingErrorStateError,
} from '../actions/calendarPublishing';

export default orchestrator(loadCalendarPublishingPermissions, async msg => {
    let folderId = msg.folderId;
    setInitialPublishPermissionsPending(true);
    let data: GetCalendarSharingPermissionsResponse = await getPublishingPermissionsService(
        folderId
    );
    setInitialPublishPermissionsPending(false);
    if (data) {
        let { Recipients, IsAnonymousSharingEnabled, AnonymousSharingMaxDetailLevel } = data;

        let notPublishedItem: PublishingPermissionItemState = {
            folderId: folderId,
            isUnpublishPending: false,
            isResetLinksPending: false,
            isPublishingAllowed: true,
        };
        notPublishedItem.CurrentDetailLevel = 'Disabled';

        let publishingRecipientInfoModels: PublishingPermissionItemState[] = [];
        if (isConsumer()) {
            publishingRecipientInfoModels = createRecipientInfoModelsForConsumer(
                notPublishedItem,
                Recipients,
                IsAnonymousSharingEnabled,
                AnonymousSharingMaxDetailLevel
            );
        } else {
            publishingRecipientInfoModels = createRecipientInfoModelsForEnterprise(
                notPublishedItem,
                Recipients,
                IsAnonymousSharingEnabled,
                AnonymousSharingMaxDetailLevel
            );
        }

        saveCalendarPublishingPermissions(
            publishingRecipientInfoModels,
            msg.folderId,
            msg.calendarTitle,
            msg.calendarHexColor
        );
    } else {
        setCalendarPublishingErrorStateError(CalendarPublishingError.InitialLoadFailure);
    }
});

function createRecipientInfoModelsForEnterprise(
    notPublishedItem: PublishingPermissionItemState,
    Recipients: CalendarSharingPermissionInfo[],
    IsAnonymousSharingEnabled: boolean,
    AnonymousSharingMaxDetailLevel: string
): PublishingPermissionItemState[] {
    let publishingRecipientInfoModel = notPublishedItem;
    let isPublicCalendarPublished = false;

    for (var i = 0; i < Recipients.length; i++) {
        publishingRecipientInfoModel = { ...notPublishedItem, ...Recipients[i] };

        if (!publishingRecipientInfoModel.FromPermissionsTable && IsAnonymousSharingEnabled) {
            // the allowed detail levels doesn't explicitly include 'Disabled' so we add it to the list
            publishingRecipientInfoModel.AllowedDetailLevels.splice(0, 0, 'Disabled');
            isPublicCalendarPublished = true;
            break;
        }
    }

    // In case Anonymous sharing is enabled but the user didn't publish it yet
    // Create a fake sharing model (the allowed permissions for publishing is different than for sharing)
    if (IsAnonymousSharingEnabled && !isPublicCalendarPublished) {
        publishingRecipientInfoModel = notPublishedItem;
        publishingRecipientInfoModel.AllowedDetailLevels = getAvailablePublishingDetailLevelsFromMaxLevel(
            AnonymousSharingMaxDetailLevel
        );
    }

    return [publishingRecipientInfoModel];
}

function createRecipientInfoModelsForConsumer(
    notPublishedItem: PublishingPermissionItemState,
    Recipients: CalendarSharingPermissionInfo[],
    IsAnonymousSharingEnabled: boolean,
    AnonymousSharingMaxDetailLevel: string
): PublishingPermissionItemState[] {
    let publishingRecipientInfoModels: PublishingPermissionItemState[] = [];
    let publishingRecipientInfoModel = notPublishedItem;
    let isPublicCalendarPublished = false;

    for (var i = 0; i < Recipients.length; i++) {
        publishingRecipientInfoModel = { ...notPublishedItem, ...Recipients[i] };

        if (!publishingRecipientInfoModel.FromPermissionsTable && IsAnonymousSharingEnabled) {
            // for consumers, since publishing the same calendar with multiple permissions is allowed, we always have exactly
            // two permission levels- AvailabilityOnly and FullDetails
            publishingRecipientInfoModel.AllowedDetailLevels = getConsumerAvailablePublishingDetailLevels();
            publishingRecipientInfoModels.push(publishingRecipientInfoModel);
            isPublicCalendarPublished = true;
        }
    }

    // In case Anonymous sharing is enabled but the user didn't publish it yet
    // Create a fake sharing model (the allowed permissions for publishing is different than for sharing)
    if (IsAnonymousSharingEnabled && !isPublicCalendarPublished) {
        publishingRecipientInfoModel = notPublishedItem;
        publishingRecipientInfoModel.AllowedDetailLevels = getConsumerAvailablePublishingDetailLevelsFromMaxLevel(
            AnonymousSharingMaxDetailLevel
        );
        return [publishingRecipientInfoModel];
    } else {
        return publishingRecipientInfoModels;
    }
}
